import { useEffect, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import TabButton from "../../components/Admin/ApplicationView/TabButton";
import SectionCard from "../../components/Admin/ApplicationView/SectionCard";
import KeyValueGrid from "../../components/Admin/ApplicationView/KeyValueGrid";
import ApplicationLogTab from "../../components/Admin/ApplicationView/ApplicationLogTab";
import {
  adminViewSections,
  incentiveClaimApplicationViewSections,
  recommenedInvestment,
} from "../../forms/incentiveClaimApplicationFields";
import api from "../../api/ApiService";
import {
  formatDate,
  formatDateTime,
  formatKeyNames,
  getKeyByValue,
  isInvalidNumber,
  mapFieldsToValues,
  numberToWords,
  stringEquals,
} from "../../utils/utils";
import UpdateStatusCard from "../../components/Admin/ApplicationView/UpdateStatusCard";
import { useLocation, useNavigate } from "react-router-dom";
import {
  amountFieldLabelsToConvertToWords,
  amountFieldsToConvertToWords,
  documentTypeMap,
  getOffciersUploadedDocumentName,
  isApplicationAfterApproval,
  totalInvestmentFields,
  totalNonEligibleFields,
} from "../../config/incentiveClaimFormUtils";
import AssignOfficerCard from "../../components/Admin/ApplicationView/AssignOfficerCard";
import {
  applicationStageList,
  applicationStageMap,
  applicationStatusDoumentTypeList,
  applicationStatusDoumentTypeMap,
  applicationStatusMap,
} from "../../config/applicationStatusConfig";
import {
  nodalOfficerDocumentTypeIdMap,
  nodalOfficersRoleMap,
} from "../../config/roleConfig";
import ApplicationSectionRenderer from "../../components/Admin/ApplicationView/ApplicationSectionRenderer";
import { getStoredItem } from "../../config/storageUtils";
import FileUploadCard from "../../components/Admin/ApplicationView/FileUploadCard";
import Button from "../../components/common/Button";
import Message from "../../components/common/Message";
import SubsidyInstallments from "../../components/Admin/ApplicationView/SubsidyInstallments";
import {
  installmentsTypes,
  schemeTypes,
} from "../../config/appliedSchemesConfig";
import EditableInvestmentInput from "../../components/Admin/ApplicationView/EditableInvestmentInput";
import TableView from "../../components/Admin/ApplicationView/TableView";
import { normalizeApplicationForExcel } from "../../utils/applicationExcelNormalizer";
import { exportApplicationExcel } from "../../utils/exportExcel";
import { useLanguage } from "../../components/LanguageProvider/LanguageProvider";

const ApplicationView = () => {
  const [formData, setFormData] = useState({});
  const location = useLocation();
  const applicationId = location.state?.applicationData?.application_id;
  const incentiveClaimId =
    location.state?.applicationData?.applied_incetive_claim_value;
  const applicationStage = location.state?.applicationData?.application_stage;
  const district = location.state?.applicationData?.district;
  const userId = location.state?.applicationData?.user_id;
  const currentStatus = location.state?.applicationData?.status_value;
  const installmentType = location.state?.applicationData?.application_type;
  const cafApprovedDate = location.state?.applicationData?.caf_approved_date;
  const cafAppliedDate = location.state?.applicationData?.caf_applied_date;
  const certificateUrl = location.state?.applicationData?.certificate_url;
  const navigate = useNavigate();
  const [docsResponse, setDocsResposne] = useState([]);
  const [unitName, setUnitName] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [apiMessage, setApiMessage] = useState(null);
  const [statusApiMessage, setStatusApiMessage] = useState(null);
  const minInvestment = 10000000;
  const [applicationTransactions, setApplicationTransactions] = useState([]);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const { language, setLanguage } = useLanguage();

  const isCapitalScheme =
    String(incentiveClaimId) ===
    schemeTypes.sreimbursementcapitalincentive.value;

  const isInterestScheme =
    String(incentiveClaimId) === schemeTypes.interestassistance.value;

  const [
    existingRecommendedCapitalSubsidy,
    setExistingRecommendedCapitalSubsidy,
  ] = useState(null);
  const [existingRecommendedStampDuty, setExistingRecommendedStampDuty] =
    useState(null);
  const [
    existingRecommendedCapitalSubsidySecondInstallment,
    setExistingRecommendedCapitalSubsidySecondInstallment,
  ] = useState(null);
  const [
    existingRecommendedInterestSubsidy,
    setExistingRecommendedInterestSubsidy,
  ] = useState(null);
  const [
    existingRecommendedInterestSubsidySecondInstallment,
    setExistingRecommendedInterestSubsidySecondInstallment,
  ] = useState(null);
  const [
    existingRecommendedInterestSubsidyThirdInstallment,
    setExistingRecommendedInterestSubsidyThirdInstallment,
  ] = useState(null);

  const stageName = Object.values(applicationStageMap).find(
    (stage) => stage.value === applicationStage,
  )?.label;

  const showUpdateStatusCard = stageName
    ? location?.state?.fromPage?.includes(stageName?.toLowerCase())
    : false;

  const applicationDetailsSampleData = {
    "Tracking Number": location.state?.applicationData?.tracking_number || "",
    "CAF ID": location.state?.applicationData?.caf_id || "N/A",
    "CAF Applied Date": cafAppliedDate || "N/A",
    "CAF Approved Date": cafApprovedDate || "N/A",
    District: district || "N/A",
    "Application Number":
      location.state?.applicationData?.application_number || "",
    "Applied Date":
      formatDateTime(location.state?.applicationData?.created_at) || "",
    "Applied Scheme": location.state?.applicationData?.category || "N/A",
    Address: location.state?.applicationData?.address || "N/A",
    "Phone Number": location.state?.applicationData?.mobile_number || "N/A",
    "Email Address": location.state?.applicationData?.email || "N/A",
  };

  useEffect(() => {
    const getUserDetails = async () => {
      const resp = await api.get("/user-full-data");
      if (resp.status === 200) {
        setUserDetails(resp?.data?.user_details);
      }
    };
    getUserDetails();
  }, []);

  useEffect(() => {
    const getApplicationData = async () => {
      const responseData = await api.get(`/get-caf/${applicationId}`);
      let mainData = responseData.data || {};

      // --- Get uploaded documents ---
      const responseDocs = await api.post(`/sub-categories/documents`, {
        category_id: 1,
        application_id: applicationId,
        user_id: userId,
      });

      const docData = {};
      if (responseDocs?.data) {
        responseDocs.data.forEach((doc) => {
          const docTypeId = doc?.id;
          const fieldName = getKeyByValue(documentTypeMap, doc?.name_en);
          if (doc.upload_documents?.length > 0) {
            const uploaded = doc.upload_documents[0];
            docData[fieldName] = {
              url: uploaded.path,
              id: uploaded.id,
              name: uploaded.filename || "",
              label: doc.name_en,
              docTypeId: docTypeId || "",
            };
          } else {
            docData[fieldName] = {
              url: "",
              id: "",
              name: "",
              label: doc.name_en,
              docTypeId: docTypeId || "",
            };
          }
        });
      }

      let maxAmount = 0;

      if (mainData.area_category === "A") {
        maxAmount = 400000;
      } else if (mainData.area_category === "B") {
        maxAmount = 500000;
      } else if (mainData.area_category === "C") {
        maxAmount = 600000;
      }

      let initialData = {
        ...mainData,
        ...docData,
        maximum_eligible_amount_first_installment: maxAmount,
        maximum_eligible_amount_second_installment: maxAmount,
        maximum_eligible_amount_third_installment: maxAmount,
        investment_certificate: {
          name: "InvestmentCertificate.pdf",
          id: "",
          url: certificateUrl || "",
          label: "Investment Certificate",
        },
      };

      amountFieldsToConvertToWords.forEach((field) => {
        if (initialData[field]) {
          initialData[`${field}_in_words`] = numberToWords(initialData[field]);
        }
      });

      setExistingRecommendedCapitalSubsidy(
        mainData.recommended_investment || "",
      );
      setExistingRecommendedStampDuty(
        mainData.stamp_duty_recommended_investment || "",
      );
      setExistingRecommendedCapitalSubsidySecondInstallment(
        mainData.recommended_capital_subsidy_second_installment || "",
      );
      setExistingRecommendedInterestSubsidy(
        mainData.recommended_interest_subsidy_first_installment || "",
      );
      setExistingRecommendedInterestSubsidySecondInstallment(
        mainData.recommended_interest_subsidy_second_installment || "",
      );
      setExistingRecommendedInterestSubsidyThirdInstallment(
        mainData.recommended_interest_subsidy_third_installment || "",
      );

      setFormData({
        ...initialData,
      });
      setUnitName(mainData?.unit_name || "");
      setDocsResposne(responseDocs.data || []);
    };
    getApplicationData();
    getApplicationTransactions();
  }, [userDetails?.id]);

  const getApplicationTransactions = async () => {
    try {
      const response = await api.get(`/transactions/${applicationId}`);
      if (response.status === 200) {
        setApplicationTransactions(response.data || []);
      }
    } catch (error) {}
  };

  const getSanctionOrderFieldKey = () => {
    if (
      String(incentiveClaimId) ===
      schemeTypes.sreimbursementcapitalincentive.value
    ) {
      if (installmentType === "1")
        return "sanction_order_capital_subsidy_first_installment";
      if (installmentType === "2")
        return "sanction_order_capital_subsidy_second_installment";
    }

    if (String(incentiveClaimId) === schemeTypes.interestassistance.value) {
      if (installmentType === "1")
        return "sanction_order_interest_assistance_first_installment";
      if (installmentType === "2")
        return "sanction_order_interest_assistance_second_installment";
      if (installmentType === "3")
        return "sanction_order_interest_assistance_third_installment";
    }

    return null;
  };

  const transactionFirstInstallment = applicationTransactions?.find(
    (txn) => txn.installment === installmentsTypes.first.value,
  );

  const transactionSecondInstallment = applicationTransactions?.find(
    (txn) => txn.installment === installmentsTypes.second.value,
  );

  const handleAssignOfficer = async (nextOfficer, remarks, file) => {
    try {
      const officerConfig = nodalOfficersRoleMap[nextOfficer.toLowerCase()];
      const stageValue =
        officerConfig?.value ||
        applicationStageList.find(
          (a) => a.label.toLowerCase() === nextOfficer.toLowerCase(),
        )?.value;

      let finalRemarks = remarks || "";
      let uploadFilePath = "";

      if (file) {
        const sanctionField = getSanctionOrderFieldKey();
        if (!sanctionField) return;

        const documentName = nodalOfficerDocumentTypeIdMap[sanctionField];

        const documentTypeId = docsResponse?.find(
          (doc) => doc.name_en === documentName,
        )?.id;

        if (!documentTypeId)
          return setStatusApiMessage(
            "error",
            "No document_type_id mapping found for:",
            documentName,
          );

        const existingDocId =
          docsResponse?.find((d) => d?.name_en === documentName)
            ?.upload_documents[0]?.id || "";

        const fileFormData = new FormData();
        fileFormData.append("upload_doc", file);
        fileFormData.append("upload_date", formatDate(new Date()));
        fileFormData.append("category_id", 1);
        fileFormData.append("document_type_id", documentTypeId);
        fileFormData.append("application_id", applicationId);
        fileFormData.append("upload_document_id", existingDocId);
        fileFormData.append("user_id", userId);

        const uploadResponse = await api.post(
          "/application/document/upload",
          fileFormData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (!uploadResponse?.success || !uploadResponse?.data?.path) {
          return setStatusApiMessage({
            type: "error",
            message: "File upload failed",
          });
        } else {
          uploadFilePath = uploadResponse.data.path;
        }
      }

      // Only fetch pending docs if next officer is 'user' and status is pending doc
      if (
        nextOfficer.toLowerCase() ===
          applicationStageMap.user.label.toLowerCase() &&
        String(currentStatus) ===
          applicationStatusMap.incompleteDocumentPending.value
      ) {
        try {
          const logsResponse = await api.post("/user/application/logs", {
            application_id: applicationId,
            user_id: userId,
          });

          if (logsResponse?.data && Array.isArray(logsResponse.data)) {
            const latestLog = logsResponse.data
              .filter(
                (log) =>
                  log.status ===
                  applicationStatusMap.incompleteDocumentPending.value,
              )
              .sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime(),
              )[0];

            if (latestLog?.document_type_id) {
              const docIds = latestLog.document_type_id
                .split(",")
                .map((id) => id.trim());

              const pendingDocNames = docIds
                .map((id, index) => {
                  const match = docsResponse.find(
                    (doc) => String(doc.id) === id,
                  );
                  return match ? `${index + 1}. ${match.name_en}` : null;
                })
                .filter(Boolean);

              if (pendingDocNames.length > 0) {
                finalRemarks =
                  "\n\nIncomplete or Pending Documents:\n" +
                  pendingDocNames.join("\n");
              }
            }
          }
        } catch (err) {}
      }

      const stageChangeResponse = await api.post(
        "/categories/applications/stage/change",
        {
          application_id: applicationId,
          application_stage: stageValue,
          remark: finalRemarks,
          status: stageValue === applicationStageMap.user.value && ![
              applicationStatusDoumentTypeMap.incompleteDocumentPending.value,
              applicationStatusDoumentTypeMap.incompleteInformation.value,
              applicationStatusDoumentTypeMap.dlctUnverified.value,
            ].includes(String(currentStatus)) ? applicationStatusMap.nodalIncompleteInformation.value : currentStatus,
          uploaded_report: uploadFilePath,
        },
      );

      if (stageChangeResponse?.status === 200) {
        navigate("/admin/all-applications");
      }
    } catch (error) {
      setStatusApiMessage({
        type: "error",
        message: error.response?.data?.message || "Status update failed",
      });
    }
  };

  const handleUpdateStatus = async (data) => {
    try {
      if (!data.reportFile) {
        return setStatusApiMessage({
          type: "error",
          message: "Report file is mandatory",
        });
      }

      const documentKey = getOffciersUploadedDocumentName({
        status: data.status,
        incentiveClaimId: String(incentiveClaimId),
        installmentNo: parseInt(installmentType),
      });

      const documentName = documentKey
        ? nodalOfficerDocumentTypeIdMap[documentKey]
        : null;

      const documentTypeId = docsResponse?.find(
        (doc) => doc.name_en === documentName,
      )?.id;

      if (!documentTypeId)
        return setStatusApiMessage(
          "error",
          "No document_type_id mapping found for:",
          documentName,
        );

      const existingDocId =
        docsResponse?.find((d) => d?.name_en === documentName)
          ?.upload_documents[0]?.id || "";

      const fileFormData = new FormData();
      fileFormData.append("upload_doc", data.reportFile);
      fileFormData.append("upload_date", formatDate(new Date()));
      fileFormData.append("category_id", 1);
      fileFormData.append("document_type_id", documentTypeId);
      fileFormData.append("application_id", applicationId);
      fileFormData.append("upload_document_id", existingDocId);
      fileFormData.append("user_id", userId);

      const uploadResponse = await api.post(
        "/application/document/upload",
        fileFormData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (!uploadResponse?.success || !uploadResponse?.data?.path) {
        return setStatusApiMessage({
          type: "error",
          message: "File upload failed",
        });
      }

      const statusChangeResponse = await api.post(
        "/categories/applications/status/change",
        {
          application_id: applicationId,
          status: data.status,
          remarks: data.remarks || "",
          uploaded_report: uploadResponse.data.path,
          application_stage: "",
          // data.status === applicationStatusMap.approved.value
          //   ? applicationStageMap.ddo.value
          //   : "",
          document_type_id: data?.pendingDocs?.join(",") || "",
        },
      );

      if (statusChangeResponse?.status === 200) {
        navigate(`/admin/${stageName?.toLowerCase()}/applications`);
      }
    } catch (error) {
      setStatusApiMessage({ type: "error", message: "Status update failed" });
    }
  };

  const updateRecommenedInvestment = async () => {
    try {
      const postUpdate = async (payload) => {
        const response = await api.post(
          "/admin/application/update-recommended-investment",
          payload,
        );

        setApiMessage({
          type:
            response?.status === 200 || response?.status === true
              ? "success"
              : "error",
          message:
            response?.status === 200 || response?.status === true
              ? "Recommended Investment updated successfully"
              : "Failed to update Recommended Investment",
        });
      };

      const noChangeError = () =>
        setApiMessage({
          type: "error",
          message: "No changes made to Recommended Investment values",
        });

      /* ===========================
       CAPITAL SUBSIDY – FIRST
      ============================ */
      if (
        isCapitalScheme &&
        installmentType === installmentsTypes.first.value
      ) {
        const capValue = parseFloat(
          formData[recommenedInvestment.capitalSubsidy.name],
        );
        const maxCapValue = parseFloat(
          formData.total_eligible_investment_after_non_eligible_deduction,
        );

        const stampValue = parseFloat(
          formData[recommenedInvestment.stampDuty.name],
        );
        const maxStampValue = parseFloat(formData.total_stamp_duty_per_year);

        if (capValue < minInvestment) {
          return setApiMessage({
            type: "error",
            message: "Recommended Capital Subsidy must be at least 1 Crore",
          });
        }

        if (capValue > maxCapValue) {
          return setApiMessage({
            type: "error",
            message: `Recommended Capital Subsidy must not exceed Total Eligible Investment (${maxCapValue})`,
          });
        }

        if (stampValue > maxStampValue && maxStampValue > 0) {
          return setApiMessage({
            type: "error",
            message: `Recommended Stamp Duty must not exceed Total Stamp Duty Per Year (${maxStampValue})`,
          });
        }

        if (
          !formData[recommenedInvestment.capitalSubsidy.name] ||
          (!formData[recommenedInvestment.stampDuty.name] && maxStampValue > 0)
        ) {
          return setApiMessage({
            type: "error",
            message: "Please enter valid Recommended values",
          });
        }

        if (
          existingRecommendedCapitalSubsidy ===
            formData[recommenedInvestment.capitalSubsidy.name] &&
          existingRecommendedStampDuty ===
            formData[recommenedInvestment.stampDuty.name]
        ) {
          return noChangeError();
        }

        return postUpdate({
          application_id: applicationId,
          recommended_investment:
            formData[recommenedInvestment.capitalSubsidy.name],
          stamp_duty_recommended_investment:
            formData[recommenedInvestment.stampDuty.name],
          capital_subsidy_min_investment:
            formData.capital_subsidy_min_investment,
          capital_subsidy_additional_investment:
            formData.capital_subsidy_additional_investment,
          total_max_capital_subsidy: formData.total_max_capital_subsidy,
          total_eligible_investment_after_non_eligible_deduction:
            formData.total_eligible_investment_after_non_eligible_deduction,
          status: currentStatus,
          application_stage: applicationStage,
          type: incentiveClaimId,
          installment_no: installmentType,
        });
      }

      /* ===========================
       CAPITAL SUBSIDY – SECOND
      ============================ */
      if (
        isCapitalScheme &&
        installmentType === installmentsTypes.second.value
      ) {
        const value =
          formData[recommenedInvestment.capitalSubsidySecondInstallment.name];

        if (isInvalidNumber(value)) {
          return setApiMessage({
            type: "error",
            message:
              "Please enter Recommended Capital Subsidy for Second Installment",
          });
        }

        const capValue = parseFloat(value);
        const maxCapValue =
          parseFloat(formData?.total_max_capital_subsidy) -
          parseFloat(transactionFirstInstallment?.amount || 0);

        if (capValue > maxCapValue) {
          return setApiMessage({
            type: "error",
            message: `Recommended Capital Subsidy must not exceed Remaining Capital Subsidy (${maxCapValue})`,
          });
        }

        if (existingRecommendedCapitalSubsidySecondInstallment === value) {
          return noChangeError();
        }

        return postUpdate({
          application_id: applicationId,
          status: currentStatus,
          application_stage: applicationStage,
          recommended_capital_subsidy_second_installment: value,
          remaining_capital_subsidy_second_installment: maxCapValue,
          type: incentiveClaimId,
          installment_no: Number(installmentType),
        });
      }

      /* ===========================
       INTEREST ASSISTANCE – 1 / 2 / 3
      ============================ */
      if (isInterestScheme) {
        const config = {
          [installmentsTypes.first.value]: {
            field: recommenedInvestment.interestSubsidyFirstInstallment.name,
            maxField: "total_interest_paid_first_installment",
            existing: existingRecommendedInterestSubsidy,
            payloadKey: "recommended_interest_subsidy_first_installment",
            message:
              "Recommended First Installment must not exceed Total Interest Paid First Installment",
          },
          [installmentsTypes.second.value]: {
            field: recommenedInvestment.interestSubsidySecondInstallment.name,
            maxField: "total_interest_paid_second_installment",
            existing: existingRecommendedInterestSubsidySecondInstallment,
            payloadKey: "recommended_interest_subsidy_second_installment",
            message:
              "Recommended Second Installment must not exceed Total Interest Paid Second Installment",
          },
          [installmentsTypes.third.value]: {
            field: recommenedInvestment.interestSubsidyThirdInstallment.name,
            maxField: "total_interest_paid_third_installment",
            existing: existingRecommendedInterestSubsidyThirdInstallment,
            payloadKey: "recommended_interest_subsidy_third_installment",
            message:
              "Recommended Third Installment must not exceed Total Interest Paid Third Installment",
          },
        };

        const cfg = config[installmentType];
        if (!cfg) return;

        const value = formData[cfg.field];

        if (isInvalidNumber(value)) {
          return setApiMessage({
            type: "error",
            message: "Please enter Recommended Interest Subsidy amount",
          });
        }

        const capValue = parseFloat(value);
        const maxCapValue = parseFloat(formData?.[cfg.maxField] || 0);

        if (capValue > maxCapValue) {
          return setApiMessage({
            type: "error",
            message: `${cfg.message} (${maxCapValue})`,
          });
        }

        if (cfg.existing === value) {
          return noChangeError();
        }

        return postUpdate({
          application_id: applicationId,
          status: currentStatus,
          application_stage: applicationStage,
          [cfg.payloadKey]: value,
          type: incentiveClaimId,
          installment_no: Number(installmentType),
        });
      }
    } catch (err) {
      setApiMessage({
        type: "error",
        message: "An error occurred while updating Recommended Investment",
      });
    }
  };

  const isInterest =
    String(incentiveClaimId) === schemeTypes.interestassistance.value;
  const isReimbursement =
    String(incentiveClaimId) ===
    schemeTypes.sreimbursementcapitalincentive.value;

  const isFirstInstallment = installmentType === installmentsTypes.first.value;
  const isSecondInstallment =
    installmentType === installmentsTypes.second.value;
  const isThirdInstallment = installmentType === installmentsTypes.third.value;

  const dynamicTabs = [
    { key: "details", label: { en: "Application Details", hi: "आवेदन विवरण" } },
    ...incentiveClaimApplicationViewSections
      .map((section, i) => {
        let title = { ...section.title }; // always start as object

        if (isInterest && isFirstInstallment) {
          title = {
            en: title.en.replace("Stamp Duty", "Subsidy Details"),
            hi: title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण"),
          };
        } else if (isReimbursement && isSecondInstallment) {
          title = {
            en: title.en.replace("Stamp Duty", "Tax Documents"),
            hi: title.hi.replace("स्टाम्प शुल्क", "कर दस्तावेज़"),
          };
        } else if (isInterest && (isSecondInstallment || isThirdInstallment)) {
          title = {
            en: title.en.replace("Stamp Duty", "Subsidy Details"),
            hi: title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण"),
          };
        }
        // else: leave title unchanged

        return {
          key: `section-${i}`,
          label: title, // always object
          section,
        };
      })
      .filter(
        (section) =>
          !(
            isReimbursement &&
            section.label.en.toLowerCase().includes("interest subsidy")
          ),
      ),
    {
      key: "subsidy",
      label: { en: "Subsidy Evaluation", hi: "सब्सिडी मूल्यांकन" },
    },
    { key: "log", label: { en: "Logs", hi: "लॉग्स" } },
  ];

  const getDisbursedValue = (key, txn) => {
    if (key === "Disbursed Capital Subsidy - First Installment") {
      return transactionFirstInstallment?.amount ?? "--";
    }

    if (key === "Disbursed Date - Capital Subsidy First Installment") {
      return transactionFirstInstallment?.created_at
        ? formatDate(transactionFirstInstallment.created_at)
        : "--";
    }

    if (key === "Remaining Capital Subsidy Second Installment") {
      return (
        parseFloat(formData?.total_max_capital_subsidy) -
        parseFloat(transactionFirstInstallment?.amount || 0)
      );
    }

    return null;
  };

  const canEditRecommendedInvestment = ({
    key,
    value,
    userDetails,
    applicationStage,
    currentStatus,
    installmentType,
    formData,
  }) => {
    const isCapitalOrStamp =
      stringEquals(
        key,
        language
          ? recommenedInvestment.capitalSubsidy.label.hi
          : recommenedInvestment.capitalSubsidy.label.en,
      ) ||
      stringEquals(
        key,
        language
          ? recommenedInvestment.capitalSubsidySecondInstallment.label.hi
          : recommenedInvestment.capitalSubsidySecondInstallment.label.en,
      ) ||
      stringEquals(
        key,
        language
          ? recommenedInvestment.stampDuty.label.hi
          : recommenedInvestment.stampDuty.label.en,
      ) ||
      (stringEquals(
        key,
        language
          ? recommenedInvestment.interestSubsidyFirstInstallment.label.hi
          : recommenedInvestment.interestSubsidyFirstInstallment.label.en,
      ) &&
        installmentType === installmentsTypes.first.value) ||
      (stringEquals(
        key,
        language
          ? recommenedInvestment.interestSubsidySecondInstallment.label.hi
          : recommenedInvestment.interestSubsidySecondInstallment.label.en,
      ) &&
        installmentType === installmentsTypes.second.value) ||
      (stringEquals(
        key,
        language
          ? recommenedInvestment.interestSubsidyThirdInstallment.label.hi
          : recommenedInvestment.interestSubsidyThirdInstallment.label.en,
      ) &&
        installmentType === installmentsTypes.third.value);

    const hasPermission =
      (!userDetails?.role_id && applicationStage === null) ||
      String(userDetails?.role_id) === nodalOfficersRoleMap.tifc.value ||
      String(userDetails?.role_id) === nodalOfficersRoleMap.slac.value;

    return (
      isCapitalOrStamp &&
      value &&
      hasPermission &&
      currentStatus !== applicationStatusMap.paymentDone.value &&
      !isApplicationAfterApproval(
        currentStatus,
        applicationStage,
        installmentType,
        formData,
        incentiveClaimId,
      )
    );
  };

  const shouldHideEvaluationSection = ({
    key,
    title,
    schemeId,
    installmentType,
  }) => {
    const isScheme = (type) => schemeId === schemeTypes[type].value;
    const isInstallment = (type) =>
      installmentType === installmentsTypes[type].value;
    // Stamp Duty / Capital Subsidy hidden for Interest Assistance
    if (
      (key === "stampduty" || key.includes("capitalsubsidy")) &&
      isScheme("interestassistance")
    ) {
      return true;
    }

    // Capital Subsidy - Second Installment hidden for first installment
    if (key === "capitalsubsidysecondinstallment" && isInstallment("first")) {
      return true;
    }

    // Stamp Duty & Capital Subsidy First Installment hidden for reimbursement scheme + second installment
    if (
      (key === "stampduty" || key === "capitalsubsidyfirstinstallment") &&
      isScheme("sreimbursementcapitalincentive") &&
      isInstallment("second")
    ) {
      return true;
    }

    // Interest subsidy sections only for interest assistance
    if (key.includes("interestsubsidy") && !isScheme("interestassistance")) {
      return true;
    }

    // Interest Subsidy 2nd & 3rd hidden for first installment
    if (
      (title.includes("interest subsidy - second installment") ||
        title.includes("interest subsidy - third installment")) &&
      isScheme("interestassistance") &&
      isInstallment("first")
    ) {
      return true;
    }

    // Interest Subsidy 3rd hidden for second installment
    if (
      title.includes("interest subsidy - third installment") &&
      isScheme("interestassistance") &&
      isInstallment("second")
    ) {
      return true;
    }

    return false;
  };

  const handleExcelDownload = () => {
    try {
      setLoadingExcel(true);
      const rows = normalizeApplicationForExcel({
        sections: incentiveClaimApplicationViewSections,
        formData,
        incentiveClaimId: incentiveClaimId,
        installmentType: installmentType,
      });
      setLoadingExcel(false);

      exportApplicationExcel(rows, "Incentive_Claim.xlsx");
    } catch (err) {
      setLoadingExcel(false);
    }
  };

  const [activeTab, setActiveTab] = useState(dynamicTabs[0].key);

  return (
    <DashboardLayout pageTitle="Profile" mainClass="p-6">
      <div className="flex justify-end h-10 mb-4 mr-6">
        <Button
          onClick={handleExcelDownload}
          disabled={loadingExcel}
          className={`bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm`}
        >
          {loadingExcel ? "Downloading..." : "Download Excel"}
        </Button>
      </div>
      {/* Tabs */}
      <div className="flex flex-col sm:flex-row sm:space-x-3 space-y-2 sm:space-y-0 p-4 sm:p-6">
        {dynamicTabs.map((tab) => (
          <TabButton
            key={tab.key}
            label={tab.label}
            isActive={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            language={language}
          />
        ))}
      </div>

      <section className="px-6 py-2">
        {/* Application Details */}
        {activeTab === "details" && (
          <div className="bg-white rounded p-3">
            <SectionCard
              title={language ? "आवेदन विवरण : " : "APPLICATION DETAILS :"}
              color="blue"
            >
              <KeyValueGrid data={applicationDetailsSampleData} />
            </SectionCard>
          </div>
        )}

        {/* Dynamic Sections */}
        {dynamicTabs
          .filter((tab) => tab.key.startsWith("section-"))
          .map((tab) =>
            activeTab === tab.key ? (
              <ApplicationSectionRenderer
                ionRenderer
                key={tab.key}
                section={tab.section}
                formData={formData}
                setFormData={setFormData}
                incentiveClaimId={incentiveClaimId}
                installmentType={installmentType}
                language={language}
              />
            ) : null,
          )}

        {/* Subsidy Evaluation Tab */}
        {activeTab === "subsidy" && (
          <>
            <div className="bg-white rounded p-3 mt-4">
              {Object.entries(adminViewSections)
                .filter(([sectionKey, section]) => {
                  const key = sectionKey.toLowerCase();
                  const title = section.title.en.toLowerCase();
                  const schemeId = String(incentiveClaimId);
                  if (
                    shouldHideEvaluationSection({
                      key,
                      title,
                      schemeId,
                      installmentType,
                    })
                  ) {
                    return false;
                  }

                  return true;
                })
                .map(([sectionKey, section]) => (
                  <SectionCard
                    key={sectionKey}
                    title={language ? section.title.hi : section.title.en}
                    color={sectionKey === "capitalSubsidy" ? "blue" : "green"}
                  >
                    {section.subSections?.map((sub, subIndex) => {
                      if (
                        sectionKey === "stampDuty" &&
                        (formData["total_stamp_duty_per_year"] === 0 ||
                          !formData["total_stamp_duty_per_year"])
                      ) {
                        return (
                          <div key={subIndex}>
                            {language ? "योग्य नहीं" : "Not Eligible"}
                          </div>
                        );
                      }

                      const mappedData = mapFieldsToValues(
                        sub.fields,
                        formData,
                        language,
                      );
                      if (!mappedData) return null;

                      return (
                        <div
                          key={subIndex}
                          className="mt-6 border border-gray-200 rounded p-4 bg-gray-50"
                        >
                          {sub.title && (
                            <h4 className="font-bold text-theme-color mb-3 text-xl">
                              {isApplicationAfterApproval(
                                currentStatus,
                                applicationStage,
                                installmentType,
                                formData,
                                incentiveClaimId,
                              ) && sub.title.en === "Tentative Capital Subsidy"
                                ? language
                                  ? "अनुमानित पूंजी सब्सिडी"
                                  : "Approved Capital Subsidy"
                                : language
                                  ? sub.title.hi
                                  : sub.title.en}
                            </h4>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                            {Object.entries(mappedData).map(([key, value]) => {
                              if (
                                Array.isArray(value) &&
                                value.length > 0 &&
                                typeof value[0] === "object"
                              ) {
                                return (
                                  <TableView
                                    label={key}
                                    value={value}
                                    language={language}
                                  />
                                );
                              }

                              const showInWords =
                                amountFieldLabelsToConvertToWords.includes(key);

                              const disbursedValue = getDisbursedValue(key);

                              const wordValue =
                                showInWords && (disbursedValue || value)
                                  ? numberToWords(disbursedValue || value)
                                  : null;

                              const isEditable = canEditRecommendedInvestment({
                                key,
                                value,
                                userDetails,
                                applicationStage,
                                currentStatus,
                                installmentType,
                                formData,
                              });

                              return (
                                <div key={key} className="mb-4">
                                  <p>
                                    <strong>{key}</strong>
                                  </p>

                                  {isEditable ? (
                                    <EditableInvestmentInput
                                      keyLabel={key}
                                      formData={formData}
                                      setFormData={setFormData}
                                      wordValue={wordValue}
                                      language={language}
                                    />
                                  ) : (
                                    <>
                                      <span>
                                        {disbursedValue ?? value ?? "--"}
                                      </span>
                                      {wordValue && (
                                        <p className="text-sm text-theme-color mt-1">
                                          {language
                                            ? wordValue.hi
                                            : wordValue.en}
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </SectionCard>
                ))}

              {/* Update Recommended Investment Button */}
              {((!userDetails?.role_id && applicationStage === null) ||
                String(userDetails?.role_id) ===
                  nodalOfficersRoleMap.tifc.value ||
                String(userDetails?.role_id) ===
                  nodalOfficersRoleMap.slac.value) &&
                currentStatus !== applicationStatusMap.paymentDone.value &&
                !isApplicationAfterApproval(
                  currentStatus,
                  applicationStage,
                  installmentType,
                  formData,
                  incentiveClaimId,
                ) && (
                  <div className="w-full flex items-center gap-4 mt-4">
                    <div className="w-1/2">
                      {apiMessage && (
                        <Message
                          apiMessage={apiMessage}
                          setApiMessage={setApiMessage}
                        />
                      )}
                    </div>
                    <div className="w-1/2 flex justify-end">
                      <Button
                        onClick={updateRecommenedInvestment}
                        className="px-4 py-2 rounded bg-btn text-white"
                      >
                        {language
                          ? "अनुशंसित निवेश अपडेट करें"
                          : "Update Recommended Investment"}
                      </Button>
                    </div>
                  </div>
                )}
            </div>

            {/* Officers Uploaded Reports */}
            {docsResponse?.some(
              (doc) =>
                Object.values(nodalOfficerDocumentTypeIdMap).includes(
                  doc.name_en,
                ) && doc.upload_documents?.length > 0,
            ) && (
              <div className="bg-white rounded p-3 mt-4">
                <SectionCard
                  title={
                    language
                      ? "अधिकारियों ने रिपोर्ट अपलोड कीं"
                      : "Officers Uploaded Reports"
                  }
                  color="blue"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {docsResponse
                      .filter(
                        (doc) =>
                          Object.values(nodalOfficerDocumentTypeIdMap).includes(
                            doc.name_en,
                          ) && doc.upload_documents?.length > 0,
                      )
                      .map((doc) => {
                        const uploaded = doc.upload_documents[0];
                        return (
                          <FileUploadCard
                            key={uploaded?.id || doc.id}
                            label={doc.name_en}
                            fileUrl={uploaded?.path || ""}
                            fileName={uploaded?.filename || ""}
                            isEdit={false}
                          />
                        );
                      })}
                  </div>
                </SectionCard>
              </div>
            )}

            {/* Subsidy Installments */}
            {String(userDetails?.role_id) ===
              nodalOfficersRoleMap.ddo.value && (
              <SubsidyInstallments
                formData={formData}
                setFormData={setFormData}
                incentiveClaimId={incentiveClaimId}
                applicationId={applicationId}
                unitName={unitName}
                district={district}
                userId={userId}
                status={currentStatus}
                docsResponse={docsResponse}
                installmentType={installmentType}
              />
            )}

            {/* Status / Assign Section */}
            <div className="mt-4">
              {statusApiMessage && (
                <Message
                  apiMessage={statusApiMessage}
                  setApiMessage={setStatusApiMessage}
                />
              )}
            </div>

            {((!stageName &&
              currentStatus !== applicationStatusMap.paymentDone.value) ||
              (showUpdateStatusCard &&
                String(userDetails?.role_id) !==
                  nodalOfficersRoleMap.ddo.value)) && (
              <div className="bg-white rounded p-3 mt-4">
                <SectionCard
                  title={language ? "" : "Update Status"}
                  color="blue"
                >
                  {stageName ? (
                    <UpdateStatusCard
                      stage={stageName}
                      onUpdate={handleUpdateStatus}
                      pendingDocumentList={docsResponse.filter(
                        (doc) => doc.upload_documents.length > 0,
                      )}
                      isInterestScheme={isInterestScheme}
                    />
                  ) : (
                    <AssignOfficerCard
                      formData={formData}
                      onAssign={handleAssignOfficer}
                      status={currentStatus}
                      incentiveClaimId={incentiveClaimId}
                      installmentType={installmentType}
                    />
                  )}
                </SectionCard>
              </div>
            )}
          </>
        )}

        {/* Logs */}
        {activeTab === "log" && (
          <div className="bg-white rounded p-3">
            <ApplicationLogTab
              applicationId={applicationId}
              userId={userId}
              docsResponse={docsResponse}
            />
          </div>
        )}
      </section>
    </DashboardLayout>
  );
};

export default ApplicationView;
